import apiClient from '../../shared/auth/services'

/**
 * Speech Recognition Service
 * Handles voice recognition API calls
 */
export const speechRecognitionService = {
  /**
   * Send audio data for speech recognition
   * @param {string} audioData - Base64 encoded audio data
   * @param {number} satsueiMemoCategoryId - Category ID for field mapping
   * @param {object} options - Optional configuration
   * @returns {Promise<object>} Recognition result with transcript and extracted data
   */
  async recognizeSpeech(audioData, satsueiMemoCategoryId, options = {}) {
    try {
      const response = await apiClient.post('/recognizeSpeech', {
        audioData,
        satsueiMemoCategoryId,
        encoding: options.encoding || 'WEBM_OPUS',
        sampleRateHertz: options.sampleRateHertz || 48000,
        languageCode: options.languageCode || 'ja-JP'
      })
      return response.data
    } catch (error) {
      console.error('Speech recognition error:', error)
      throw error
    }
  },

  /**
   * Get predefined voice keywords list
   * @returns {Promise<object>} Map of keyword to description
   */
  async getVoiceKeywords() {
    try {
      const response = await apiClient.get('/getVoiceKeywords')
      return response.data
    } catch (error) {
      console.error('Failed to get voice keywords:', error)
      throw error
    }
  },

  /**
   * Get voice recognition mappings for a category
   * @param {number} satsueiMemoCategoryId - Category ID
   * @param {number} satsueiMemoColumnId - Optional column ID for single lookup
   * @returns {Promise<object>} Mapping list
   */
  async getVoiceRecognitionMapping(satsueiMemoCategoryId, satsueiMemoColumnId = null) {
    try {
      const response = await apiClient.post('/getVoiceRecognitionMapping', {
        satsueiMemoCategoryId,
        satsueiMemoColumnId
      })
      return response.data
    } catch (error) {
      console.error('Failed to get voice recognition mappings:', error)
      throw error
    }
  },

  /**
   * Register or update voice recognition mapping
   * @param {object} data - Mapping data
   * @returns {Promise<object>} Registration result
   */
  async registVoiceRecognitionMapping(data) {
    try {
      const response = await apiClient.post('/registVoiceRecognitionMapping', data)
      return response.data
    } catch (error) {
      console.error('Failed to register voice recognition mapping:', error)
      throw error
    }
  },

  /**
   * Remove voice recognition mapping
   * @param {object} data - Mapping data with IDs
   * @returns {Promise<object>} Removal result
   */
  async removeVoiceRecognitionMapping(data) {
    try {
      const response = await apiClient.post('/removeVoiceRecognitionMapping', data)
      return response.data
    } catch (error) {
      console.error('Failed to remove voice recognition mapping:', error)
      throw error
    }
  }
}

export default speechRecognitionService
